import { DateTime, FixedOffsetZone } from 'luxon';

import { internationalizedDate } from './string-utilities';

export interface DateTimeDisplay {
  startDate: string;
  startTime: string;
  startTimeSimple: string;
  startTimeAMPM: string;
  endDate: string;
  endTime: string;
  endTimeSimple: string;
  endTimeAMPM: string;
  offset: string;
  timezone: string;
  relativeTime?: string;
}

/** Parse many utcOffset shapes into minutes. Accepts:
 *  - "UTC-07:00", "-07:00", "+0530", "+05:30", "Z"
 *  - number minutes: -420, 330
 */
function parseUtcOffsetToMinutes(utcOffset: string | number): number | null {
  if (utcOffset == (null as any)) return null;

  if (typeof utcOffset === 'number' && Number.isFinite(utcOffset)) {
    return Math.trunc(utcOffset);
  }

  if (typeof utcOffset === 'string') {
    const s = utcOffset.trim().toUpperCase();
    if (s === 'Z' || s === 'UTC' || s === 'UTC+0' || s === 'UTC-0') return 0;

    // Strip leading "UTC"
    const body = s.startsWith('UTC') ? s.slice(3) : s;

    // Normalize "+HH:mm" | "-HH:mm" | "+HHMM" | "-HHMM"
    const m = body.match(/^([+-])\s*(\d{1,2})(?::?(\d{2}))?$/);
    if (m) {
      const sign = m[1] === '-' ? -1 : 1;
      const hh = parseInt(m[2], 10);
      const mm = m[3] ? parseInt(m[3], 10) : 0;
      if (hh <= 23 && mm <= 59) return sign * (hh * 60 + mm);
    }
  }

  return null;
}

/**
 * Priority:
 * 1) utcOffset provided -> use fixed-offset zone from it (UTC±HH:mm)
 * 2) else if timezone provided -> use IANA zone
 * 3) else -> keep embedded offset from input
 */
export function createDateTimeDisplay(
  startDate: DateTime | string,
  endDate?: DateTime | string,
  relative?: boolean,
  use12HourFormat?: boolean,
  locale?: string,
  timezone?: string | null,
  utcOffset?: string | number, // actual UTC offset to apply (minutes or string forms)
  showSeconds?: boolean
): DateTimeDisplay {
  const dateTimeDisplay = {} as DateTimeDisplay;
  const systemTime = DateTime.now();

  // Parse with embedded zone respected
  const startRaw =
    startDate instanceof DateTime
      ? startDate
      : DateTime.fromISO(startDate as any, { setZone: true });

  // Build target zone
  const fixedMinutes =
    utcOffset != null ? parseUtcOffsetToMinutes(utcOffset) : null;

  const startTarget =
    fixedMinutes !== null
      ? startRaw.setZone(FixedOffsetZone.instance(fixedMinutes))
      : timezone
        ? startRaw.setZone(timezone)
        : startRaw;

  const start = locale ? startTarget.setLocale(locale) : startTarget;

  // End (if any)
  const endRaw =
    endDate instanceof DateTime
      ? endDate
      : endDate
        ? DateTime.fromISO(endDate as any, { setZone: true })
        : null;

  const endTarget = endRaw
    ? fixedMinutes !== null
      ? endRaw.setZone(FixedOffsetZone.instance(fixedMinutes))
      : timezone
        ? endRaw.setZone(timezone)
        : endRaw
    : null;

  const endDt = endTarget && locale ? endTarget.setLocale(locale) : endTarget;

  if (start?.isValid) {
    // Show offset badge only if different to system
    dateTimeDisplay.offset =
      start.offset === systemTime.offset
        ? (null as any)
        : (start.toFormat('ZZ') as any);

    // Zone label: show "UTC±HH:mm" when using fixed offset; else use zoneName
    dateTimeDisplay.timezone =
      fixedMinutes !== null ? `UTC${start.toFormat('ZZ')}` : start.zoneName;

    const startTimeFormat =
      use12HourFormat && showSeconds
        ? DateTime.TIME_WITH_SECONDS
        : use12HourFormat
          ? DateTime.TIME_SIMPLE
          : showSeconds
            ? DateTime.TIME_24_WITH_SECONDS
            : DateTime.TIME_24_SIMPLE;
    dateTimeDisplay.startTime = start.toLocaleString(startTimeFormat);

    const startTimeSimpleFormat = showSeconds
      ? DateTime.TIME_WITH_SECONDS
      : DateTime.TIME_SIMPLE;
    dateTimeDisplay.startTimeSimple = start
      .toLocaleString(startTimeSimpleFormat)
      .replace(/\s[AP]M$/, '');

    dateTimeDisplay.startTimeAMPM = start.toFormat('a');

    dateTimeDisplay.startDate = internationalizedDate(
      start,
      'CONDENSED_DATE',
      relative,
      null,
      locale
    );
  }

  if (endDt?.isValid) {
    const endTimeFormat =
      use12HourFormat && showSeconds
        ? DateTime.TIME_WITH_SECONDS
        : use12HourFormat
          ? DateTime.TIME_SIMPLE
          : showSeconds
            ? DateTime.TIME_24_WITH_SECONDS
            : DateTime.TIME_24_SIMPLE;
    dateTimeDisplay.endTime = endDt.toLocaleString(endTimeFormat);

    dateTimeDisplay.endTimeAMPM = endDt.toFormat('a');

    const endTimeSimpleFormat = showSeconds
      ? DateTime.TIME_WITH_SECONDS
      : DateTime.TIME_SIMPLE;
    dateTimeDisplay.endTimeSimple = endDt
      .toLocaleString(endTimeSimpleFormat)
      .replace(/\s[AP]M$/, '');

    dateTimeDisplay.endDate = internationalizedDate(
      endDt,
      'CONDENSED_DATE',
      relative,
      null,
      locale
    );
  }

  return dateTimeDisplay;
}
